import { CheckCircleIcon } from '@heroicons/react/24/solid';
import type { DxReportOrthopulseStatus } from '../types';

interface OrthopulseStatusBarProps {
    status: DxReportOrthopulseStatus;
    onChange: (next: DxReportOrthopulseStatus) => void;
    readOnly?: boolean;
}

interface StatusOption {
    id: DxReportOrthopulseStatus;
    label: string;
    description: string;
    /** Border + background applied when the option is active. */
    activeContainer: string;
    /** Label text color when the option is active. */
    activeLabelText: string;
    /** Description text color when the option is active. */
    activeDescText: string;
    /** Check-icon tint when the option is active. */
    activeIconText: string;
}

const STATUS_OPTIONS: readonly StatusOption[] = [
    {
        id: 'mandatory',
        label: 'Mandatory',
        description: 'Required for this case. The Orthopulse fee is locked on for the GP.',
        activeContainer: 'border-amber-500 bg-amber-50 shadow-sm',
        activeLabelText: 'text-amber-900',
        activeDescText: 'text-amber-800',
        activeIconText: 'text-amber-600'
    },
    {
        id: 'recommended',
        label: 'Recommended',
        description: 'Suggested for this case. Toggle defaults on; GP can opt out.',
        activeContainer: 'border-emerald-500 bg-emerald-50 shadow-sm',
        activeLabelText: 'text-emerald-900',
        activeDescText: 'text-emerald-800',
        activeIconText: 'text-emerald-600'
    },
    {
        id: 'not_needed',
        label: 'Not needed',
        description: 'Not indicated. Toggle defaults off and the fee is dropped.',
        activeContainer: 'border-slate-500 bg-slate-100 shadow-sm',
        activeLabelText: 'text-slate-900',
        activeDescText: 'text-slate-700',
        activeIconText: 'text-slate-600'
    }
] as const;

/**
 * Status selector for the Orthopulse recommendation sub-step. Three chips
 * with a short rationale; click to change. Persists into the Dx report
 * timeline payload via `setOrthopulseStatus` (which also keeps the fees
 * addon catalog in sync).
 *
 * Active state uses semantic tints (amber / emerald / slate) so the choice
 * is readable at a glance — picking Mandatory looks meaningfully different
 * from Recommended even before you read the label.
 */
export function OrthopulseStatusBar({ status, onChange, readOnly }: OrthopulseStatusBarProps) {
    return (
        <div className="px-4 py-3 bg-gray-50/40 border-b border-gray-200 flex-shrink-0">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Orthopulse status
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {STATUS_OPTIONS.map((option) => {
                    const active = status === option.id;
                    return (
                        <button
                            key={option.id}
                            type="button"
                            disabled={readOnly}
                            onClick={() => onChange(option.id)}
                            aria-pressed={active}
                            className={
                                // border-2 always so the active state's thicker border
                                // doesn't shift the layout when toggling between cards.
                                'text-left rounded-lg border-2 px-3 py-2.5 transition-colors ' +
                                (active
                                    ? option.activeContainer
                                    : 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50') +
                                (readOnly ? ' opacity-60 cursor-not-allowed' : '')
                            }
                        >
                            <div className="flex items-center gap-1.5">
                                <span
                                    className={
                                        'text-sm ' +
                                        (active
                                            ? 'font-bold ' + option.activeLabelText
                                            : 'font-semibold text-gray-700')
                                    }
                                >
                                    {option.label}
                                </span>
                                {active && (
                                    <CheckCircleIcon
                                        className={'w-4 h-4 ' + option.activeIconText}
                                    />
                                )}
                            </div>
                            <div
                                className={
                                    'text-[11px] leading-snug mt-0.5 ' +
                                    (active ? option.activeDescText : 'text-gray-500')
                                }
                            >
                                {option.description}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
